import { useEffect, useState } from 'react';

export interface TimeLeft {
  days: string;
  hours: string;
  minutes: string;
  seconds: string;
  isExpired: boolean;
  isStarted: boolean;
}

export const useCountdown = (
  startDate?: string,
  endDate?: string,
): TimeLeft => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({
    days: '00',
    hours: '00',
    minutes: '00',
    seconds: '00',
    isExpired: false,
    isStarted: false,
  });

  useEffect(() => {
    if (!endDate) return;

    const startTimestamp = startDate ? new Date(startDate).getTime() : 0;
    const endTimestamp = new Date(endDate).getTime();

    const interval = setInterval(() => {
      const now = Date.now();
      let distance = 0;
      let started = true;
      let expired = false;

      if (startDate && now < startTimestamp) {
        // "Pending" phase
        distance = startTimestamp - now;
        started = false;
      } else if (now < endTimestamp) {
        // "Running" phase
        distance = endTimestamp - now;
        started = true;
      } else {
        // "Expired" phase
        distance = 0;
        started = true;
        expired = true;
        clearInterval(interval);
      }

      setTimeLeft({
        days: Math.floor(distance / (1000 * 60 * 60 * 24))
          .toString()
          .padStart(2, '0'),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
          .toString()
          .padStart(2, '0'),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))
          .toString()
          .padStart(2, '0'),
        seconds: Math.floor((distance % (1000 * 60)) / 1000)
          .toString()
          .padStart(2, '0'),
        isExpired: expired,
        isStarted: started,
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [startDate, endDate]);

  return timeLeft;
};
